import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArchiveRestore,
  ArrowRight,
  BookMarked,
  Droplets,
  Hammer,
  Leaf,
  LockKeyhole,
  MessageCircle,
  PackageCheck,
  Send,
  ShieldCheck,
  ShoppingBasket,
  Trash2,
  UsersRound,
} from 'lucide-react';
import { Store } from './mirror-store.js';
import { getMirrorPartyPosts, postMirrorPartyPhoto } from './ai.js';
import { PrivateImage } from './private-media.jsx';
import { getSurroundingAttention, normalizeSurroundingDetails } from './surrounding-config.js';

const BOARD_CONFIG = {
  garden: {
    title: 'Garden board',
    placeholder: 'Write a care update, a question, or something worth remembering…',
    prompts: ['Care update', 'Propagation note', 'Question for the house'],
    promptHints: ['What changed since the last check?', 'What are you growing next?', 'What would help this plant thrive?'],
    icon: Leaf,
    attentionTitle: 'What wants attention',
  },
  library: {
    title: 'Book board',
    placeholder: 'Keep a thought, mark a pause, or start a conversation about this book…',
    prompts: ['Reading note', 'Quote to revisit', 'Discuss with the party'],
    promptHints: ['What stayed with you?', 'Add the passage in your own words.', 'What should everyone weigh in on?'],
    icon: BookMarked,
    attentionTitle: 'On the reading table',
  },
  storage: {
    title: 'Storage board',
    placeholder: 'Leave a retrieval note, a box update, or a future decision…',
    prompts: ['Retrieval note', 'Box update', 'Where should this live?'],
    promptHints: ['Make the next search shorter.', 'What moved or changed?', 'Ask before this gets buried again.'],
    icon: ArchiveRestore,
    attentionTitle: 'Worth opening again',
  },
  pantry: {
    title: 'Pantry board',
    placeholder: 'Post a use-soon idea, restock note, or plan for what is already here…',
    prompts: ['Use-soon idea', 'Restock note', 'Meal plan'],
    promptHints: ['Turn what is near its date into a plan.', 'What is below your minimum?', 'Build a meal from the shelf.'],
    icon: ShoppingBasket,
    attentionTitle: 'Use what is here',
  },
  care: {
    title: 'Care board',
    placeholder: 'Record a confirmed instruction, maintenance note, or household reminder…',
    prompts: ['Verified instruction', 'Maintenance note', 'Household reminder'],
    promptHints: ['Repeat only what you confirmed.', 'What needs cleaning, replacing, or servicing?', 'Share a practical reminder deliberately.'],
    icon: ShieldCheck,
    attentionTitle: 'Review with care',
  },
  objects: {
    title: 'Objects board',
    placeholder: 'Post a project update, the story behind an object, or what should happen next…',
    prompts: ['Project update', 'Story behind it', 'Offer or ask'],
    promptHints: ['What changed or still needs doing?', 'Why does this stay?', 'Offer it, ask for help, or find it a home.'],
    icon: Hammer,
    attentionTitle: 'Things in motion',
  },
};

const POST_TYPES = {
  garden: 'care',
  library: 'event',
  storage: 'reminder',
  pantry: 'reminder',
  care: 'care',
  objects: 'event',
};

function uid() {
  return globalThis.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function relativeTime(value) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  if (Math.abs(seconds) < 60) return formatter.format(seconds, 'second');
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour');
  return formatter.format(Math.round(hours / 24), 'day');
}

function focusRows(branch, items) {
  const prioritized = [...items].sort((a, b) => Number(!!getSurroundingAttention(b)) - Number(!!getSurroundingAttention(a)));
  return prioritized.map(item => {
    const detail = normalizeSurroundingDetails(item.surrounding);
    const attention = getSurroundingAttention(item);
    if (branch === 'garden') return { item, icon: Droplets, label: attention?.label || detail.lightNeeds || 'Care rhythm ready', meta: detail.nextActionAt || detail.lastWateredAt || '' };
    if (branch === 'library') return { item, icon: BookMarked, label: detail.readingStatus === 'reading' ? 'Reading now' : detail.currentChapter || 'On your shelf', meta: detail.currentPage ? `Page ${detail.currentPage}` : '' };
    if (branch === 'storage') return { item, icon: ArchiveRestore, label: attention?.label || detail.boxCode ? `${attention?.label || 'Stored'}${detail.boxCode ? ` · ${detail.boxCode}` : ''}` : 'Location recorded', meta: detail.reviewAt || detail.storageArea || '' };
    if (branch === 'pantry') return { item, icon: PackageCheck, label: attention?.label || (detail.minimumStock ? 'Minimum set' : 'In stock'), meta: detail.expiryDate || (detail.quantity ? `${detail.quantity} ${detail.unit}`.trim() : '') };
    if (branch === 'care') return { item, icon: ShieldCheck, label: detail.labelVerified ? (attention?.label || 'Label checked') : 'Needs label review', meta: detail.nextActionAt || detail.careCategory || '' };
    return { item, icon: Hammer, label: detail.objectDisposition ? `Next: ${detail.objectDisposition}` : attention?.label || 'Remembered here', meta: detail.shareMode !== 'private' ? `Intent: ${detail.shareMode}` : '' };
  }).slice(0, 4);
}

function BoardNote({ post, linkedItem, onOpenItem, onDelete }) {
  return <article className={`surrounding-board-note ${post.destination === 'self' ? 'is-private' : 'is-party'}`}>
    <header><span className="board-note-avatar">{String(post.author || 'Me').slice(0, 1).toUpperCase()}</span><span><strong>{post.author || 'Me'}</strong><small>{post.destinationLabel || 'Only me'} · {relativeTime(post.createdAt)}</small></span>{post.destination === 'self' && onDelete ? <button type="button" onClick={() => onDelete(post.id)} aria-label="Delete private board note"><Trash2 /></button> : <UsersRound />}</header>
    {post.prompt ? <span className="board-note-prompt">{post.prompt}</span> : null}
    <p>{post.body}</p>
    {linkedItem ? <button type="button" className="board-note-record" onClick={() => onOpenItem?.(linkedItem)}><PrivateImage source={linkedItem.imageData} mediaId={linkedItem.imageMediaId} fallback="./assets/silver-knit.webp" alt="" /><span><strong>{linkedItem.label || 'Linked record'}</strong><small>Open record</small></span><ArrowRight /></button> : null}
  </article>;
}

function StarterNote({ label, hint, index }) {
  return <article className="surrounding-board-note is-starter" aria-label={`${label} starter note`}>
    <span className="starter-note-index">0{index + 1}</span>
    <span className="board-note-prompt">{label}</span>
    <p>{hint}</p>
    <small>Choose this prompt above, then leave a note for future you or your party.</small>
  </article>;
}

export default function SurroundingBoard({ branch, items = [], state, refresh, aiSession, onOpenItem, showToast }) {
  const config = BOARD_CONFIG[branch] || BOARD_CONFIG.objects;
  const BoardIcon = config.icon;
  const parties = aiSession?.parties || [];
  const partyKey = parties.map(party => `${party.id}:${party.name}`).join('|');
  const [body, setBody] = useState('');
  const [prompt, setPrompt] = useState(config.prompts[0]);
  const [linkedItemId, setLinkedItemId] = useState('');
  const [destination, setDestination] = useState('self');
  const [remotePosts, setRemotePosts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setBody('');
    setPrompt(config.prompts[0]);
    setLinkedItemId('');
    setDestination('self');
  }, [branch, config.prompts]);

  useEffect(() => {
    let active = true;
    if (!aiSession?.token || !parties.length) {
      setRemotePosts([]);
      return () => { active = false; };
    }
    Promise.all(parties.map(async party => {
      try {
        const result = await getMirrorPartyPosts(party.id, 100);
        return (result.posts || [])
          .filter(post => String(post.eventKey || '').startsWith(`surrounding-board:${branch}:`))
          .map(post => ({
            id: `party:${party.id}:${post.id}`,
            body: post.note,
            prompt: String(post.title || '').split(' · ').slice(1).join(' · '),
            author: post.displayName,
            destination: 'party',
            destinationLabel: party.name,
            createdAt: post.createdAt,
          }));
      } catch {
        return [];
      }
    })).then(groups => {
      if (active) setRemotePosts(groups.flat());
    });
    return () => { active = false; };
  }, [aiSession?.token, branch, partyKey, reload]);

  const privatePosts = useMemo(() => (state?.surroundingBoards || [])
    .filter(post => post.branch === branch)
    .map(post => ({ ...post, author: 'Me', destinationLabel: 'Only me' })), [branch, state?.surroundingBoards]);
  const posts = useMemo(() => [...privatePosts, ...remotePosts]
    .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))
    .slice(0, 9), [privatePosts, remotePosts]);
  const linkedItemMap = useMemo(() => new Map(items.map(item => [String(item.id), item])), [items]);
  const attentionRows = useMemo(() => focusRows(branch, items), [branch, items]);
  const selectedParty = destination.startsWith('party:') ? parties.find(party => String(party.id) === destination.slice(6)) : null;
  const promptIndex = Math.max(0, config.prompts.indexOf(prompt));
  const authorInitial = String(aiSession?.user?.displayName || aiSession?.user?.username || 'Me').slice(0, 1).toUpperCase();

  const submit = async event => {
    event.preventDefault();
    const note = body.trim();
    if (!note || busy) return;
    setBusy(true);
    try {
      if (!selectedParty) {
        Store.saveSurroundingBoardPost({ branch, body: note, prompt, linkedItemId, destination: 'self' });
        refresh?.();
        showToast?.(`Saved privately to ${config.title}.`);
      } else {
        const linkedItem = linkedItemMap.get(linkedItemId);
        const sharedNote = linkedItem ? `${note}\nLinked record: ${linkedItem.label || 'Untitled record'}` : note;
        await postMirrorPartyPhoto(selectedParty.id, {
          eventKey: `surrounding-board:${branch}:${uid()}`,
          title: `${config.title} · ${prompt}`,
          eventDate: new Date().toISOString().slice(0, 10),
          postType: POST_TYPES[branch] || 'event',
          note: sharedNote,
        });
        setReload(value => value + 1);
        showToast?.(`Posted to ${selectedParty.name}.`);
      }
      setBody('');
      setLinkedItemId('');
    } catch (error) {
      showToast?.(error.message || 'That board note could not be posted.');
    } finally {
      setBusy(false);
    }
  };

  const deletePrivate = useCallback(postId => {
    Store.deleteSurroundingBoardPost(postId);
    refresh?.();
    showToast?.('Private board note removed.');
  }, [refresh, showToast]);

  if (branch === 'extra') return null;

  return <section className="surrounding-board" aria-labelledby={`surrounding-${branch}-board-title`}>
    <div className="surrounding-board-main">
      <div className="surrounding-board-heading"><div><BoardIcon /><span><small>A shared surface, kept deliberate</small><strong id={`surrounding-${branch}-board-title`}>{config.title}</strong><p>Keep a thought for yourself, or place it with one Sync Party.</p></span></div><span>{posts.length ? `${posts.length} recent ${posts.length === 1 ? 'note' : 'notes'}` : 'Ready for its first note'}</span></div>
      <form className="surrounding-board-composer" onSubmit={submit}>
        <div className="board-compose-row">
          <span className="board-composer-avatar" aria-hidden="true">{authorInitial}</span>
          <label className="board-compose-copy"><MessageCircle /><textarea value={body} onChange={event => setBody(event.target.value)} placeholder={config.placeholder} maxLength="400" rows="1" aria-label={`Write on ${config.title}`} /></label>
          <label className="board-compose-select"><span>Linked record</span><select value={linkedItemId} onChange={event => setLinkedItemId(event.target.value)}><option value="">None</option>{items.map(item => <option key={item.id} value={item.id}>{item.label || 'Untitled record'}</option>)}</select></label>
          <label className="board-compose-select"><span>Audience</span><select value={destination} onChange={event => setDestination(event.target.value)}><option value="self">Only me</option>{parties.map(party => <option value={`party:${party.id}`} key={party.id}>{party.name}</option>)}</select></label>
          <button className="board-post-button" type="submit" disabled={!body.trim() || busy}><Send /> <span>{busy ? 'Posting…' : 'Post note'}</span></button>
        </div>
        <div className="board-compose-meta">
          <div className="board-prompt-rail" role="group" aria-label={`${config.title} note prompts`}>{config.prompts.map(label => <button type="button" className={prompt === label ? 'active' : ''} onClick={() => setPrompt(label)} key={label}>{label}</button>)}</div>
          <div className="board-privacy-line">{selectedParty ? <><UsersRound /><span>Visible to <strong>{selectedParty.name}</strong> only.</span></> : <><LockKeyhole /><span>Private to <strong>only me</strong>.</span></>}</div>
        </div>
        <p className="board-prompt-hint">{config.promptHints[promptIndex]}</p>
      </form>
      {posts.length ? <div className="surrounding-board-notes">{posts.map(post => <BoardNote key={post.id} post={post} linkedItem={linkedItemMap.get(String(post.linkedItemId))} onOpenItem={onOpenItem} onDelete={post.destination === 'self' ? deletePrivate : undefined} />)}</div> : <div className="surrounding-board-notes is-empty">{config.prompts.map((label, index) => <StarterNote key={label} label={label} hint={config.promptHints[index]} index={index} />)}</div>}
    </div>
    <aside className="surrounding-board-attention" aria-labelledby={`surrounding-${branch}-attention-title`}><div><BoardIcon /><span><small>In this workspace</small><strong id={`surrounding-${branch}-attention-title`}>{config.attentionTitle}</strong></span></div>{attentionRows.length ? <div>{attentionRows.map(({ item, icon: RowIcon, label, meta }) => <button type="button" onClick={() => onOpenItem?.(item)} key={item.id}><PrivateImage source={item.imageData} mediaId={item.imageMediaId} fallback="./assets/silver-knit.webp" alt="" /><span><strong>{item.label || 'Untitled record'}</strong><small>{label}{meta ? ` · ${meta}` : ''}</small></span><RowIcon /></button>)}</div> : <p>Add a record and its next attention will gather here.</p>}</aside>
  </section>;
}
